import { useState } from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";

import FieldChip from "../FieldChip/FieldChip";
import { useDeleteDataFieldMutation } from "../../hooks/mutations";
import { DeleteConfirmModal } from "../../../DeleteConfirmationModal";
import { DRAG_DATA_FIELD, dataFieldChipId } from "../../dnd/kinds";

/**
 * Each chip is both a draggable and a droppable, sharing one id/kind
 * (DRAG_DATA_FIELD) for both roles — the central handler in
 * useQueryBuilderDnd.js resolves chip-to-chip reordering from that shared
 * tag regardless of which hook registered it.
 *
 * No live "siblings slide to make room" animation here: the chips are
 * variable-width text in a wrapping row, and a sortable reflow strategy
 * can't reliably predict how the row re-wraps when widths differ, which
 * looked like labels swapping instead of chips sliding. Instead, whichever
 * chip currently resolves as the reorder target (isOver) gets a plain
 * highlight — a stable indicator with nothing to animate incorrectly.
 */
export default function DataFieldChip({ ruleId, queryId, dataField, index, canEdit }) {
  const [deleteOpened, setDeleteOpened] = useState(false);
  const { mutate: deleteDataField } = useDeleteDataFieldMutation({ ruleId });

  const chipId = dataFieldChipId(dataField.id);
  const data = { kind: DRAG_DATA_FIELD, dataField, index, queryId };

  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: chipId,
    data,
    disabled: !canEdit,
  });

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: chipId,
    data,
    disabled: !canEdit,
  });

  const setRef = (node) => {
    setDragRef(node);
    setDropRef(node);
  };

  return (
    <>
      <FieldChip
        ruleId={ruleId}
        entityField={dataField?.entityField}
        canEdit={canEdit}
        drag={canEdit}
        onDelete={() => setDeleteOpened(true)}
        dragRef={setRef}
        dragHandleProps={{ ...attributes, ...listeners }}
        dragStyle={{
          opacity: isDragging ? 0.4 : 1,
          outline: isOver
            ? "2px solid var(--mantine-primary-color-filled, var(--mantine-color-blue-6))"
            : "2px solid transparent",
          outlineOffset: 2,
          borderRadius: 4,
        }}
      />

      <DeleteConfirmModal
        opened={deleteOpened}
        onClose={() => setDeleteOpened(false)}
        onDelete={() => {
          deleteDataField(dataField.id);
          setDeleteOpened(false);
        }}
        label="Delete data field?"
      />
    </>
  );
}
